import React, {useRef, useState} from 'react';
import ConfirmableButton from '../../src/components/ConfirmableButton/ConfirmableButton';
import trans from '../../src/helpers/trans';
import usePermissionGranted from '@src/hooks/usePermissionGranted';
import app from '@src/helpers/app';
import {useNavigate} from 'react-router-dom';
import LoadingIndicator from '@src/components/LoadingIndicator/LoadingIndicator';
import RenderIf from '@src/components/RenderIf/RenderIf';
import FlyErrorMessage from '@src/components/FlyErrorMessage/FlyErrorMessage';

const ConfirmButton = React.forwardRef<HTMLElement, {
    disabled?: boolean,
    onClick: React.ComponentProps<typeof ConfirmableButton>['onClick'],
    children: React.ReactNode,
}>(function ConfirmButton(props, ref) {
    const {disabled, onClick, children} = props;

    return <span ref={ref}>
        <ConfirmableButton
                size="sm"
                color="warning"
                className="me-2"
                title={trans('OrdersIndex.actions.clone-order.button-tooltip')}
                popoverTitle="OrdersIndex.actions.clone-order.confirm-popover-title"
                confirmButtonLabel="OrdersIndex.actions.clone-order.confirm-popover-confirm-button-label"
                cancelButtonLabel="OrdersIndex.actions.clone-order.confirm-popover-reject-button-label"
                disabled={disabled}
                onClick={onClick}
        >{children ?? null}</ConfirmableButton>
    </span>;
});

export default function OrderListCloneItemButton(props: {
    orderId?: number | string | null,
}) {
    const {orderId} = props;
    const controllerName = 'Orders';
    const canCloneOrder = usePermissionGranted('orders@can-clone-order');
    const navigate = useNavigate();
    const showPageRoute = app('client-router').getRouteByName(
            app('names-resolver').clientRoutes.getShowRouteName(controllerName)
    );
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const targetRef = useRef<HTMLElement>(null);

    if (!orderId || !canCloneOrder || !showPageRoute) {
        return null;
    }

    return <span onClick={e => void e.stopPropagation()}>
        <FlyErrorMessage
                error={errorMessage}
                targetRef={targetRef}
                containerRef={document.body}
                autoCloseDelay={5000}
        >
            <ConfirmButton
                    ref={targetRef}
                    disabled={isLoading}
                    onClick={() => {
                        if (isLoading) {
                            return;
                        }

                        setIsLoading(true);
                        setErrorMessage(null);

                        return app('http').route('orders.clone', {
                            id: String(orderId),
                        }, {
                            json: {'controller-name': controllerName}
                        }).then((response: { id: number | string }) => {
                            if (showPageRoute && response != null && response.id) {
                                setTimeout(
                                        url => navigate(url),
                                        100,
                                        showPageRoute.resolveUrl({id: response.id}),
                                );
                            }

                            return response;
                        }).catch(e => {
                            if (e != null && e instanceof Error) {
                                return void setErrorMessage(e.message);
                            }

                            setErrorMessage(typeof e === 'string' ? e : 'Something went wrong');
                        }).finally(() => {
                            setIsLoading(false);
                        });
                    }}
            >
                <RenderIf expression={isLoading}>
                    <LoadingIndicator type="border" size="sm"/>
                </RenderIf>
                <RenderIf expression={!isLoading}>
                    <i className="fas fa-copy"/>
                </RenderIf>
            </ConfirmButton>
        </FlyErrorMessage>
    </span>;
}
